import React, { useMemo } from 'react';
import { AppTable, ProgressBar } from '@allygpo/ally-ux';
import { getCoreRowModel, useReactTable, type ColumnDef, type Table } from '@tanstack/react-table';
import type { ScenarioResult } from '../../types';
import { count, money } from '../../utils/format';
import { sortByFamily } from '../../utils/families';
import { alignEnd, headerEnd, tableView, unpaginated } from './appTable';

export type NcrMetric = 'medicare' | 'weighted';
export type UnitMode = 'category' | 'invoice';

interface BreakdownTableProps {
  /** One scenario's product-family rows. */
  rows: readonly ScenarioResult[];
  metric: NcrMetric;
  unitMode: UnitMode;
  /** "Medicare", or "Benchmark" for oral categories. */
  ncrLabel: string;
  /** Replaces the plain family name, e.g. with a rebate link and fee-schedule warnings. */
  renderFamily?: (row: ScenarioResult, label: string) => React.ReactNode;
  /** Small second line under the "Product family" header, e.g. "Invoice unit strength". */
  familyNote?: string;
}

export const familyLabel = (name: string | null | undefined) => (name ? name.charAt(0).toUpperCase() + name.slice(1) : '—');

const num = (row: ScenarioResult, field: string): number | undefined => {
  const v = (row as unknown as Record<string, unknown>)[field];
  return v === null || v === undefined || Number.isNaN(Number(v)) ? undefined : Number(v);
};

/** What the breakdown cells read from `meta.view`. */
interface BreakdownView {
  /** "Medicare", "Benchmark" or "Weighted". */
  metricName: string;
  /** "/ EU" or "/ IU". */
  perUnit: string;
  unitsLabel: string;
  familyNote?: string;
  unitsField: string;
  reimbField: string;
  ncrField: string;
  /** "_per_eu" or "_per_iu". */
  suffix: string;
  renderFamily?: BreakdownTableProps['renderFamily'];
}

const viewOf = (table: Table<ScenarioResult>) => tableView<BreakdownView, ScenarioResult>(table);
// AppTable headers don't wrap; the long numeric ones may, so all ten columns fit without scrolling.
const WRAP_HEADER = 'white-space-normal';
// AppTable sets the table's width to the sum of the column sizes, so they're minimums small enough to fit
// the narrowest expansion (~700px at 1024 wide); the auto layout spreads the rest.
const NUM_SIZE = 56;
const share = (row: ScenarioResult) => Number(row.future_distribution ?? 0);
const negative = (value: number | undefined) => (value !== undefined && value < 0 ? 'text-danger' : undefined);

/** A right-aligned money column read from `field` (plus the per-unit suffix when `perUnit`). */
const moneyColumn = (
  id: string,
  header: (view: BreakdownView) => string,
  field: (view: BreakdownView) => string,
  options: { fallbackZero?: boolean; signed?: boolean; strong?: boolean } = {},
): ColumnDef<ScenarioResult> => ({
  id,
  size: NUM_SIZE,
  header: ({ table }) => headerEnd(header(viewOf(table))),
  meta: {
    ...alignEnd,
    headerClassName: WRAP_HEADER,
    cellClassName: ({ rowData, cell }) => {
      const value = num(rowData, field(viewOf(cell.getContext().table)));
      return [options.strong && 'font-semibold', options.signed && negative(value), 'text-nowrap'].filter(Boolean).join(' ');
    },
  },
  cell: ({ row: { original: row }, table }) => {
    const value = num(row, field(viewOf(table)));
    return money(options.fallbackZero ? value ?? 0 : value);
  },
});

const columns: ColumnDef<ScenarioResult>[] = [
  {
    id: 'family',
    size: 96,
    header: ({ table }) => {
      const { familyNote } = viewOf(table);
      return <span>Product family{familyNote && <span className="block text-2xs text-tertiary">{familyNote}</span>}</span>;
    },
    cell: ({ row: { original: row }, table }) => {
      const label = familyLabel(row.product_family_name);
      const { renderFamily } = viewOf(table);
      return renderFamily ? renderFamily(row, label) : label;
    },
  },
  {
    id: 'distribution',
    size: 96,
    header: 'Distribution',
    cell: ({ row: { original: row } }) => {
      const pct = Math.round(Math.max(0, Math.min(1, share(row))) * 100);
      return (
        <span className="flex align-items-center u-gap-2">
          {/* Muted fill for families at 0%, like the rest of their row. */}
          <ProgressBar value={pct} className="w-3rem flex-none" color={pct > 0 ? undefined : 'var(--a-s-text-tertiary)'} aria-label={`Distribution ${pct}%`} />
          {Math.round(share(row) * 100)}%
        </span>
      );
    },
  },
  {
    id: 'units',
    size: NUM_SIZE,
    header: ({ table }) => headerEnd(viewOf(table).unitsLabel),
    meta: { ...alignEnd, headerClassName: WRAP_HEADER, cellClassName: 'text-nowrap' },
    cell: ({ row: { original: row }, table }) => count(num(row, viewOf(table).unitsField)),
  },
  moneyColumn('reimb', (v) => `${v.metricName} reimb.`, (v) => v.reimbField),
  moneyColumn('reimbPerUnit', (v) => `${v.metricName} reimb. ${v.perUnit}`, (v) => `${v.reimbField}${v.suffix}`),
  moneyColumn('adminFee', () => 'Admin fee', () => 'total_admin_fee', { fallbackZero: true }),
  moneyColumn('price', () => 'Price', () => 'net_price', { fallbackZero: true }),
  moneyColumn('rebate', () => 'Rebate', () => 'total_rebate'),
  moneyColumn('ncr', (v) => `${v.metricName} NCR`, (v) => v.ncrField, { signed: true, strong: true }),
  moneyColumn('ncrPerUnit', (v) => `${v.metricName} NCR ${v.perUnit}`, (v) => `${v.ncrField}${v.suffix}`, { signed: true }),
];

/**
 * Detailed product-family breakdown for one scenario, shared by Results, Favorites and Compare.
 * Families keep the fixed (alphabetical) order; families at 0% stay in place, muted.
 * Colour means polarity only: negative NCR is red, nothing is green.
 */
const BreakdownTable: React.FC<BreakdownTableProps> = ({ rows, metric, unitMode, ncrLabel, renderFamily, familyNote }) => {
  const data = useMemo(() => sortByFamily(rows, (r) => r.product_family_name ?? ''), [rows]);
  const reimbField = metric === 'medicare' ? 'estimated_medicare_reimbursement' : 'estimated_pvt_reimbursement';
  const view: BreakdownView = {
    metricName: metric === 'medicare' ? ncrLabel : 'Weighted',
    perUnit: unitMode === 'category' ? '/ EU' : '/ IU',
    unitsLabel: unitMode === 'category' ? 'Category units' : 'Invoice units',
    familyNote,
    unitsField: unitMode === 'category' ? 'category_units' : 'invoice_units',
    reimbField,
    ncrField: metric === 'medicare' ? 'total_estimated_ncr' : 'total_weighted_ncr',
    suffix: unitMode === 'category' ? '_per_eu' : '_per_iu',
    renderFamily,
  };
  const table = useReactTable({
    ...unpaginated,
    data,
    columns,
    meta: { view },
    getRowId: (row) => `${row.scenario_id}:${row.product_family_id}`,
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <AppTable
      isBordered={false}
      size="sm"
      tableLayout="auto"
      ariaLabel="Detailed breakdown"
      table={table}
      rowClassName={({ rowData }) => (share(rowData) > 0 ? '' : 'text-tertiary')}
    />
  );
};

export default BreakdownTable;
